import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Typography from "@mui/joy/Typography";
import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea'; // Import Textarea
import ApiService from "../../axios/Axios";

interface CreateCallbackReminderModalProps {
    openState: boolean;
    parentCallback: (success: boolean, message: string) => void;
    lead_id: number | null;
}

const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
};

export default function CreateCallbackReminderModal({ openState, parentCallback, lead_id }: CreateCallbackReminderModalProps) {
    const [open, setOpen] = useState<boolean>(openState);
    const [dueDate, setDueDate] = useState<string>(getTodayString());
    const [description, setDescription] = useState<string>(''); // State for description
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setOpen(openState);
        if (openState) {
            setDueDate(getTodayString());
            setDescription('');
        }
    }, [openState]);

    async function handleCreate() {
        if (!lead_id) {
            parentCallback(false, "Error: Lead ID is missing.");
            setOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await ApiService.createCallbackReminder(lead_id, dueDate, description);
            parentCallback(true, 'Callback reminder created successfully!');
            setOpen(false);
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || "An unexpected error occurred.";
            parentCallback(false, errorMessage);
            setOpen(false);
        } finally {
            setIsLoading(false);
        }
    }

    function handleCancel() {
        setOpen(false);
        parentCallback(false, '');
    }

    return (
        <React.Fragment>
            <Modal open={open} onClose={handleCancel}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{
                    width: { xs: '90vw', sm: 'auto' },
                    maxWidth: { xs: '90vw', sm: '450px' },
                }}>
                    <DialogTitle>
                        <AddIcCallIcon sx={{ mr: 1 }} />
                        Create Callback Reminder
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <Typography>
                                Select a due date to create a "Call Back" reminder for this lead.
                            </Typography>
                            <FormControl>
                                <FormLabel>Due Date</FormLabel>
                                <Input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    slotProps={{
                                        input: {
                                            min: getTodayString(),
                                        },
                                    }}
                                    required
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Description (Optional)</FormLabel>
                                <Textarea
                                    minRows={3}
                                    placeholder="Add a note or specific details for the callback..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </FormControl>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="solid"
                            color="primary"
                            loading={isLoading}
                            onClick={handleCreate}
                        >
                            Create
                        </Button>
                        <Button variant="plain" color="neutral" onClick={handleCancel} disabled={isLoading}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    );
}